<template>
  <SfTable
    :headers="headers"
    :rows="rows"
    :handles-sorting="false"
    :non-sortable-headers="nonSortableHeaders"
    :initial-sort-by="sortBy"
    :initial-sort-direction="sortDirection"
    :variant="tableVariants.DS"
    class="max-lg:[&_table]:tw-table-fixed max-lg:[&_table]:tw-w-full"
    :sticky-header="true"
    data-testid="users-table"
    @table-sort="emit('sort', $event)"
  >
    <template #row="{ rowData, cellClasses }">
      <td
        v-for="column in columns"
        :key="column"
        class="tw-cursor-pointer tw-text-sm tw-text-sf-slate-dark-1"
        :class="cellClasses"
        :data-testid="`user-${rowData.id}-${column}`"
        @click="emit('open', rowData.id)"
      >
        <div
          v-if="column === userColumns.NAME"
          class="tw-flex tw-items-center tw-gap-2"
        >
          <SfUserAvatar :name="rowData.name" size="md" />
          <div class="tw-flex tw-min-w-0 tw-flex-col tw-gap-0.5">
            <div class="tw-flex tw-min-w-0 tw-items-center tw-gap-1">
              <SfTruncatedLabel
                :text="rowData.name"
                class="tw-font-medium tw-leading-5"
                data-testid="user-name"
              />
              <span
                v-if="isCurrentUser(rowData)"
                class="tw-shrink-0 tw-text-sf-slate-light-2"
              >
                {{ I18n.t('user.table.you') }}
              </span>
              <i
                v-if="rowData.certified"
                v-tooltip="I18n.t('user.certified_badge')"
                class="fa fa-circle-check tw-shrink-0 tw-text-sf-primary-dark"
                :aria-label="I18n.t('user.certified_badge')"
                data-testid="user-certified"
              />
            </div>
            <SfTruncatedLabel
              v-if="rowData.email"
              :text="rowData.email"
              class="tw-text-[13px] tw-leading-[18px] tw-text-sf-slate-light-2"
              data-testid="user-email"
            />
          </div>
        </div>
        <SfStaticPill
          v-else-if="column === userColumns.STATUS"
          :variant="STATUS_VARIANTS[rowData.status]"
        >
          {{ I18n.t(STATUS_KEYS[rowData.status]) }}
        </SfStaticPill>
        <template v-else-if="column === userColumns.ROLE">
          {{ I18n.t(ROLE_KEYS[rowData.role]) }}
        </template>
        <template v-else-if="column === userColumns.ACCESS">
          {{ I18n.t(ACCESS_KEYS[rowData.access]) }}
        </template>
        <template v-else-if="column === userColumns.FILES">
          {{ rowData.filesCount }}
        </template>
        <template v-else-if="column === userColumns.LAST_LOGIN">
          <span v-if="rowData.lastLogin">
            {{ SfTime(rowData.lastLogin).fromNow() }}
          </span>
          <span v-else :aria-label="I18n.t('user.table.never_logged_in')">
            &mdash;
          </span>
        </template>
        <SfButtonDs
          v-else-if="column === userColumns.ACTIONS"
          variant="tertiary"
          size="sm"
          icon-only
          icon-before="pen"
          :aria-label="I18n.t('user.table.edit_user')"
          @click.stop="emit('open', rowData.id)"
        />
      </td>
    </template>
  </SfTable>
</template>

<script setup lang="ts">
import type {
  SfTableHeaderConfig,
  TableSortEvent,
} from '@app/shared/vue/components/sf-table/types'
import type {
  SortableUserColumn,
  UserList,
  UserRow,
  UserSortDirection,
} from '@app/application/user-management/types'

import { computed } from 'vue'

import { SfTime } from '@app/adapters/time'
import {
  ACCESS_KEYS,
  COLUMN_CLASSES,
  COLUMN_LABEL_KEYS,
  EXTERNAL_COLUMNS,
  ROLE_KEYS,
  STAFF_COLUMNS,
  STATUS_KEYS,
  STATUS_VARIANTS,
} from '@app/application/user-management/constants'
import { tableVariants } from '@app/shared/vue/components/sf-table/constants'
import { isSortableUserColumn } from '@app/application/user-management/predicates'
import {
  userColumns,
  userSortDirections,
} from '@app/application/user-management/types'
import useI18n from '@app/shared/vue/composables/use-I18n'
import { SfButtonDs } from '@silverfin/ui-components'
import SfStaticPill from '@app/shared/vue/components/sf-static-pill/sf-static-pill.vue'
import SfTable from '@app/shared/vue/components/sf-table/index.vue'
import SfTruncatedLabel from '@app/shared/vue/components/sf-truncated-label.vue'
import SfUserAvatar from '@app/shared/vue/components/sf-user-avatar/sf-user-avatar.vue'

defineOptions({ name: 'UsersTable' })

interface Props {
  list: UserList
  rows: UserRow[]
  sortBy?: SortableUserColumn
  sortDirection?: UserSortDirection
}

const {
  list,
  rows,
  sortBy = userColumns.NAME,
  sortDirection = userSortDirections.ASC,
} = defineProps<Props>()

const emit =
  defineEmits<{
    open: [userId: number]
    sort: [event: TableSortEvent]
  }>()

const I18n = useI18n()

const columns = computed(() =>
  list === 'staff' ? STAFF_COLUMNS : EXTERNAL_COLUMNS
)

const nonSortableHeaders = computed(() =>
  columns.value.filter((column) => !isSortableUserColumn(column))
)

const headers = computed<SfTableHeaderConfig[]>(() =>
  columns.value.map((column) => ({
    id: column,
    label: I18n.t(COLUMN_LABEL_KEYS[column]),
    class: COLUMN_CLASSES[column],
  }))
)

const isCurrentUser = (user: UserRow): boolean =>
  String(user.id) === window.Silverfin.currentUserId()
</script>

<style scoped>
/* This header sticks to the top of the card's own scroll box, so it needs no
   offset. The uikit default assumes the whole page scrolls under the app bar. */
:deep(.sf-table-sticky th) {
  --sticky-offset: 0;
}
</style>
